import { logError } from '@/utils/logger';
import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash, ArrowUp, ArrowDown, Wallet, MagnifyingGlass, Funnel, Bank } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import ExportButton from '@/components/ExportButton';
import OperatingCosts from '@/components/OperatingCosts';
import { SortableHeader, useSort } from '@/components/SortableHeader';

const CATEGORIES = [
  { value: 'caisse_clients', label: 'Caisse Clients' },
  { value: 'caisse_social', label: 'Caisse Sociale' },
  { value: 'caisse_retenue', label: 'Caisse Retenues' },
  { value: 'salary', label: 'Salaires' },
  { value: 'equipment', label: 'Équipements' },
  { value: 'contract', label: 'Contrats' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'transport', label: 'Transport' },
  { value: 'other', label: 'Autre' },
];

const CATEGORY_COLORS = {
  caisse_clients: '#22D3EE', caisse_social: '#06B6D4', caisse_retenue: '#F97316',
  salary: '#2563EB', equipment: '#10B981', contract: '#F59E0B',
  maintenance: '#8B5CF6', transport: '#EC4899', other: '#94A3B8',
};

const CAISSE_CATEGORIES = ['caisse_clients', 'caisse_social', 'caisse_retenue'];

const Finance = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [zones, setZones] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedZone, setSelectedZone] = useState('all');
  const [userPermissions, setUserPermissions] = useState(null);
  const [userRole, setUserRole] = useState('');

  // Filters
  const [searchClient, setSearchClient] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const [formData, setFormData] = useState({
    type: 'expense', category: 'other', description: '', amount: '',
    date: new Date().toISOString().split('T')[0], client_id: 'none', zone_id: 'zone_kinshasa',
  });

  const fetchData = useCallback(async () => {
    try {
      const zoneParam = selectedZone && selectedZone !== 'all' ? `?zone_id=${selectedZone}` : '';
      const [entriesRes, summaryRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance${zoneParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance/summary${zoneParam}`, { credentials: 'include' }),
      ]);
      if (entriesRes.ok) setEntries(await entriesRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (error) { logError('Failed to fetch data:', error); }
  }, [selectedZone]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [userRes, zonesRes, clientsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, { credentials: 'include' }),
      ]);
      if (userRes.ok) {
        const userData = await userRes.json();
        setUserPermissions({...userData.permissions, effective: userData.effective_permissions || {}});
        setUserRole(userData.role);
        if (!userData.permissions.can_view_all_zones && userData.zone_id) setSelectedZone(userData.zone_id);
      }
      if (zonesRes.ok) setZones(await zonesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      await fetchData();
    } catch (error) { logError('Init:', error); }
    finally { setLoading(false); }
  }, [fetchData]);

  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);
  useEffect(() => { if (zones.length > 0) fetchData(); }, [zones.length, fetchData]);

  // ---- FILTERED ENTRIES ----
  const filteredEntries = useMemo(() => {
    return entries.filter(e => {
      const eDate = e.date?.split('T')[0] || '';
      const matchDate = (!dateFrom || eDate >= dateFrom) && (!dateTo || eDate <= dateTo);
      const matchCategory = filterCategory === 'all' || e.category === filterCategory;
      const matchClient = !searchClient || (() => {
        const q = searchClient.toLowerCase();
        const clientName = clients.find(c => c.client_id === e.client_id)?.name || '';
        return clientName.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q);
      })();
      return matchDate && matchCategory && matchClient;
    });
  }, [entries, dateFrom, dateTo, filterCategory, searchClient, clients]);

  // ---- FILTERED SUMMARY (recalculated from filtered entries) ----
  const filteredSummary = useMemo(() => {
    const income = filteredEntries.filter(e => e.type === 'income').reduce((s, e) => s + (e.amount || 0), 0);
    const expenses = filteredEntries.filter(e => e.type === 'expense').reduce((s, e) => s + (e.amount || 0), 0);
    const byCaisse = {};
    CAISSE_CATEGORIES.forEach(cat => {
      const catIncome = filteredEntries.filter(e => e.category === cat && e.type === 'income').reduce((s, e) => s + (e.amount || 0), 0);
      const catExpense = filteredEntries.filter(e => e.category === cat && e.type === 'expense').reduce((s, e) => s + (e.amount || 0), 0);
      byCaisse[cat] = { income: catIncome, expense: catExpense, balance: catIncome - catExpense };
    });
    const byCategory = {};
    filteredEntries.filter(e => e.type === 'expense').forEach(e => {
      byCategory[e.category] = (byCategory[e.category] || 0) + (e.amount || 0);
    });
    return { income, expenses, balance: income - expenses, byCaisse, byCategory };
  }, [filteredEntries]);

  // ---- Running balance ----
  const entriesWithBalance = useMemo(() => {
    let running = 0;
    return [...filteredEntries].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map(e => {
      running += e.type === 'income' ? e.amount : -e.amount;
      return { ...e, runningBalance: running };
    });
  }, [filteredEntries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount), client_id: formData.client_id === 'none' ? null : formData.client_id };
      const url = editingEntry ? `${process.env.REACT_APP_BACKEND_URL}/api/finance/${editingEntry.entry_id}` : `${process.env.REACT_APP_BACKEND_URL}/api/finance`;
      const response = await fetch(url, { method: editingEntry ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (response.ok) { toast.success(editingEntry ? 'Entrée modifiée' : 'Entrée ajoutée'); setIsDialogOpen(false); resetForm(); fetchData(); }
      else { const err = await response.json(); toast.error(err.detail || 'Erreur'); }
    } catch (error) { logError('Save:', error); toast.error('Erreur'); }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({ type: entry.type, category: entry.category, description: entry.description, amount: entry.amount.toString(), date: entry.date?.split('T')[0] || '', client_id: entry.client_id || 'none', zone_id: entry.zone_id || 'zone_kinshasa' });
    setIsDialogOpen(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance/${entryId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Entrée supprimée'); fetchData(); }
    } catch { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData({ type: 'expense', category: 'other', description: '', amount: '', date: new Date().toISOString().split('T')[0], client_id: 'none', zone_id: zones.length > 0 ? zones[0].zone_id : 'zone_kinshasa' });
    setEditingEntry(null);
  };

  const fmt = (amount) => new Intl.NumberFormat('fr-CD', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const getZoneName = (zid) => zones.find(z => z.zone_id === zid)?.name || zid;
  const getClientName = (cid) => clients.find(c => c.client_id === cid)?.name || '';
  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || cat;
  const canEdit = userPermissions?.effective?.finance?.write;
  const { sortConfig, onSort, sortData } = useSort('date', 'desc');

  const CHART_TOOLTIP_STYLE = { backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: '6px', color: '#F8FAFC' };
  const pieData = Object.entries(filteredSummary.byCategory).map(([key, value]) => ({ name: getCategoryLabel(key), value, color: CATEGORY_COLORS[key] || '#94A3B8' }));

  if (loading) return <div className="text-[#94A3B8]">{t('common.loading')}</div>;

  return (
    <div className="space-y-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">Finance</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Suivi des flux par client et par période</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {userPermissions?.can_view_all_zones && (
            <div className="w-[180px]">
              <SearchableSelect value={selectedZone} onValueChange={setSelectedZone} options={[{ value: 'all', label: 'Toutes les zones' }, ...zones.map(z => ({ value: z.zone_id, label: z.name }))]} placeholder="Toutes les zones" testId="zone-filter" />
            </div>
          )}
          <ExportButton endpoint="finance" filename="finance" label="Export Excel" />
          {canEdit && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-finance-button">
                  <Plus size={20} className="mr-2" weight="bold" /> Nouvelle entrée
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="text-2xl font-bold">{editingEntry ? 'Modifier l\'entrée' : 'Nouvelle entrée'}</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#F8FAFC]">Type</Label>
                      <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                        <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                          <SelectItem value="income" className="text-[#F8FAFC]">Revenu (Entrée)</SelectItem>
                          <SelectItem value="expense" className="text-[#F8FAFC]">Dépense (Sortie)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#F8FAFC]">Catégorie / Caisse</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                          {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value} className="text-[#F8FAFC]">{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#F8FAFC]">Montant ($)</Label>
                      <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                    </div>
                    <div>
                      <Label className="text-[#F8FAFC]">Date</Label>
                      <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">Description</Label>
                    <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required placeholder="Description de l'opération" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#F8FAFC]">Client</Label>
                      <div className="mt-1">
                        <SearchableSelect value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })} options={[{ value: 'none', label: 'Aucun client' }, ...clients.map(c => ({ value: c.client_id, label: c.name }))]} placeholder="Aucun client" searchPlaceholder="Rechercher un client..." testId="finance-client-select" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#F8FAFC]">Zone</Label>
                      <div className="mt-1">
                        <SearchableSelect value={formData.zone_id} onValueChange={(v) => setFormData({ ...formData, zone_id: v })} options={zones.map(z => ({ value: z.zone_id, label: z.name }))} placeholder="Zone" searchPlaceholder="Rechercher une zone..." testId="finance-zone-select" />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
                    <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Enregistrer</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters: Client search + Period + Category */}
      <div className="flex flex-col sm:flex-row gap-3 bg-[#0F172A] border border-[#1E293B] rounded-md p-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={searchClient} onChange={(e) => setSearchClient(e.target.value)} placeholder="Rechercher par client ou description..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9" data-testid="finance-search" />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] w-[150px]" title="Date début" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] w-[150px]" title="Date fin" />
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px] bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0F172A] border-[#1E293B]">
            <SelectItem value="all" className="text-[#F8FAFC]">Toutes catégories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value} className="text-[#F8FAFC]">{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary: Global + 3 Caisses */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="bg-[#0F172A] border border-[#10B981]/30 rounded-md p-4">
          <div className="flex items-center gap-2 mb-1"><ArrowUp size={18} className="text-[#10B981]" /><span className="text-xs text-[#94A3B8] uppercase">Total Revenus</span></div>
          <p className="text-xl font-bold text-[#10B981]">{fmt(filteredSummary.income)}</p>
        </div>
        <div className="bg-[#0F172A] border border-[#EF4444]/30 rounded-md p-4">
          <div className="flex items-center gap-2 mb-1"><ArrowDown size={18} className="text-[#EF4444]" /><span className="text-xs text-[#94A3B8] uppercase">Total Dépenses</span></div>
          <p className="text-xl font-bold text-[#EF4444]">{fmt(filteredSummary.expenses)}</p>
        </div>
        <div className="bg-[#0F172A] border border-[#2563EB]/30 rounded-md p-4">
          <div className="flex items-center gap-2 mb-1"><Wallet size={18} className="text-[#2563EB]" /><span className="text-xs text-[#94A3B8] uppercase">Solde Global</span></div>
          <p className={`text-xl font-bold ${filteredSummary.balance >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{fmt(filteredSummary.balance)}</p>
        </div>
      </div>

      {/* 3 Caisses Detail */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {CAISSE_CATEGORIES.map(cat => {
          const data = filteredSummary.byCaisse[cat] || {};
          const label = getCategoryLabel(cat);
          return (
            <div key={cat} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] }} />
                <h3 className="text-sm font-bold text-[#F8FAFC]">{label}</h3>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[#94A3B8]">Entrées</span><span className="text-[#10B981] font-medium">{fmt(data.income)}</span></div>
                <div className="flex justify-between"><span className="text-[#94A3B8]">Sorties</span><span className="text-[#EF4444] font-medium">{fmt(data.expense)}</span></div>
                <div className="flex justify-between border-t border-[#334155] pt-1.5"><span className="text-[#F8FAFC] font-medium">Solde</span><span className={`font-bold ${(data.balance || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{fmt(data.balance)}</span></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      {pieData.length > 0 && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
          <h2 className="text-lg font-bold text-[#F8FAFC] mb-4">Répartition des dépenses</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                {pieData.map((entry) => <Cell key={entry.name || entry.color} fill={entry.color} />)}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-3 mt-3">
            {pieData.map(item => (
              <div key={item.name} className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-xs text-[#94A3B8]">{item.name}: {fmt(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entries Table with running balance */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
        <div className="px-6 py-3 border-b border-[#1E293B] flex items-center justify-between">
          <span className="text-sm text-[#94A3B8]">{filteredEntries.length} opération(s) {searchClient || dateFrom || dateTo || filterCategory !== 'all' ? '(filtré)' : ''}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1E293B]/50">
              <tr>
                <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={onSort} className="px-4 py-3 text-xs" />
                <SortableHeader label="Type" sortKey="type" sortConfig={sortConfig} onSort={onSort} className="px-4 py-3 text-xs" />
                <SortableHeader label="Caisse / Catégorie" sortKey="category" sortConfig={sortConfig} onSort={onSort} className="px-4 py-3 text-xs" />
                <SortableHeader label="Client" sortKey="client_id" sortConfig={sortConfig} onSort={onSort} className="px-4 py-3 text-xs" />
                <SortableHeader label="Description" sortKey="description" sortConfig={sortConfig} onSort={onSort} className="px-4 py-3 text-xs" />
                <SortableHeader label="Montant" sortKey="amount" sortConfig={sortConfig} onSort={onSort} className="px-4 py-3 text-xs text-right" />
                <th className="px-4 py-3 text-right text-xs font-medium text-[#F8FAFC] uppercase">Solde</th>
                {canEdit && <th className="px-4 py-3 text-right text-xs font-medium text-[#F8FAFC] uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {entriesWithBalance.length === 0 ? (
                <tr><td colSpan={canEdit ? 8 : 7} className="px-6 py-8 text-center text-[#94A3B8]">Aucune opération trouvée</td></tr>
              ) : sortData(entriesWithBalance).map(entry => (
                <tr key={entry.entry_id} className="hover:bg-[#1E293B]/30 transition-colors">
                  <td className="px-4 py-3 text-[#94A3B8] text-sm whitespace-nowrap">{new Date(entry.date).toLocaleDateString('fr-FR')}</td>
                  <td className="px-4 py-3">
                    <Badge className={entry.type === 'income' ? 'bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30' : 'bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30'}>
                      {entry.type === 'income' ? 'Entrée' : 'Sortie'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[entry.category] || '#94A3B8' }} />
                      <span className="text-sm text-[#94A3B8]">{getCategoryLabel(entry.category)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#F8FAFC]">{getClientName(entry.client_id) || '-'}</td>
                  <td className="px-4 py-3 text-sm text-[#F8FAFC] max-w-[200px] truncate">{entry.description}</td>
                  <td className={`px-4 py-3 text-right font-medium text-sm ${entry.type === 'income' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {entry.type === 'income' ? '+' : '-'}{fmt(entry.amount)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold text-sm ${entry.runningBalance >= 0 ? 'text-[#F8FAFC]' : 'text-[#EF4444]'}`}>
                    {fmt(entry.runningBalance)}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(entry)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB] h-7 w-7 p-0"><Pencil size={14} /></Button>
                        {userRole === 'admin' && <Button size="sm" variant="ghost" onClick={() => handleDelete(entry.entry_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-7 w-7 p-0"><Trash size={14} /></Button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Frais de fonctionnement mensuels */}
      <OperatingCosts userRole={userRole} canEdit={canEdit} />
    </div>
  );
};

export default Finance;
